import { useEffect, useState } from 'react';
import transform from 'lodash/transform';
import find from 'lodash/find';
import orderBy from 'lodash/orderBy';

import { translate } from 'helpers/translateHelper';

import { assetMenuItems } from './constants';
import { ParameterBackup as ParameterBackupV2 } from './ParameterBackupV2';
import { ChangedParameterListV2 } from './ChangedParameterListV2';
import { RenameAsset } from './RenameAsset';
import { BulkUploadV2 } from './BulkUploadV2';
import { useSelector } from 'react-redux';
import { MuteAsset } from './MuteAsset';
import { useUserCapability, useNewCapability } from 'sharedHooks';
import { navigateToReportingPage } from './helpers';
import { DownloadMeasurements } from './DownloadMeasurements';
import { DataLogger } from './DataLogger';
import { gatewayTypes, assetType } from 'helpers/constants';
import { openGenerateReportModal } from '../../../Reports/ReportsHeader/actions';


const useHeaderMenus = (selectedAssetTypeId, asset, dataLoggerCommands, selectedAssetType) => {
    const capability = useNewCapability();
    const userCapability = useUserCapability();
    const hasEdit = capability.hasEditComponentCapability();
    const hasParameter = capability.hasParameterBackupCapability();
    const hasChangedParameterListCapability = capability.hasChangedParameterListCapability();
    const hasChangedParameterListUserCapability = userCapability.hasChangedParameterListView();
    const hasParameterBackupUserCapabaility = userCapability.hasParameterBackupView();
    const hasBulkUploadCapability = capability.hasBulkUploadCapability() && userCapability.hasbulkUploadView();
    const hasInstantReportCapability = capability.hasInstantReportCapability();
    const hasMeasurement = capability.hasMeasurementExportCapability();
    const loggerCommandList = dataLoggerCommands?.data;
    const hasDataLoggerCommands = loggerCommandList?.dataLoggerCommands?.specialLoggerCommands?.commands?.length > 0 ||
        loggerCommandList?.dataLoggerCommands?.triggerLoggerCommands?.commands?.length > 0 || loggerCommandList?.dataLoggerCommands?.subscibeUnsubscribeCommands?.commands?.length > 0;
    const hasInstantReportHidden = !hasInstantReportCapability || !asset?.isMonitored || asset?.type === assetType.MOTOR && selectedAssetType === assetType.MACHSENSOR || !(asset?.type === assetType.DRIVE || asset?.type === assetType.MOTOR);

    const allHeaderMenus = [
        {
            id: assetMenuItems.UPDATE_ASSET,
            availableCapabilityFn: (capability) => capability.hasEditChkCapability(),
            title: translate('ABB.Powertrain.Frontend.componentRenameModalTitle'),
            modalContentComponent: RenameAsset,
            disabled: !hasEdit
        },
        {
            id: assetMenuItems.EXPORT_PARAMETERSV2,
            availableCapabilityFn: (capability) => capability.hasParameterBackupCapabilityAvailable() && hasParameterBackupUserCapabaility,
            title: translate('ABB.Powertrain.Frontend.parameterBackupTitleV2'),
            modalContentComponent: ParameterBackupV2,
            hidden: !(hasParameter && hasParameterBackupUserCapabaility)
        },
        {
            id: assetMenuItems.EXPORT_MEASUREMENTS,
            availableCapabilityFn: (capability) => capability.hasNoVisibility(),
            title: translate('ABB.Powertrain.Frontend.downloadMeasurementsLabel'),
            modalContentComponent: DownloadMeasurements,
            disabled: !hasMeasurement,
            hidden: true
        },
        {
            id: assetMenuItems.CHANGED_PARAMETER_LIST_V2,
            availableCapabilityFn: (capability) => capability.hasChangedParameterListCapabilityAvailable() && hasChangedParameterListUserCapability,
            title: translate('ABB.Powertrain.Frontend.changedParametersListV2'),
            modalContentComponent: ChangedParameterListV2,
            hidden: !(hasChangedParameterListCapability && hasChangedParameterListUserCapability)
        },
        {
            id: assetMenuItems.BULK_UPLOADV2,
            availableCapabilityFn: (capability) => capability.hasBulkUploadCapabilityAvailable(),
            title: translate('ABB.Powertrain.Frontend.ptHierarchyHeaderMenuBulkUploadLabelV2'),
            modalContentComponent: BulkUploadV2,
            hidden: !hasBulkUploadCapability
        },
        {
            id: assetMenuItems.GENERATE_INSTANT_REPORT,
            availableCapabilityFn: (capability) => capability.hasInstantReportCapabilityAvailable(),
            title: translate('ABB.Powertrain.Frontend.reportGenerateInstantReportLabel'),
            menuAction: (dispatch) => { dispatch(openGenerateReportModal({ id: asset?.id, name: asset?.name, type: asset?.type })); },
            disabled: hasInstantReportHidden,
            hidden: hasInstantReportHidden
        },
        {
            id: assetMenuItems.VIEW_REPORTS_FOR_ASSET,
            title: translate('ABB.Powertrain.Frontend.reportViewReportsForAssetLabel'),
            menuAction: navigateToReportingPage,
            disabled: false
        }, {
            id: assetMenuItems.MUTE_ASSET,
            title: translate('ABB.Powertrain.Frontend.muteAssetTitle'),
            availableCapabilityFn: () => userCapability.hasNotificationSettingsCapability(),
            modalContentComponent: MuteAsset
        }, {
            id: assetMenuItems.DATA_LOGGER_TRIGGER,
            title: translate('ABB.Powertrain.Frontend.dataLoggerTitle'),
            modalContentComponent: DataLogger,
            availableCapabilityFn: () => (asset?.gatewayType === gatewayTypes.NETA21 || asset?.gatewayType === gatewayTypes.EGW02) && hasDataLoggerCommands,
            disabled: !((asset?.gatewayType === gatewayTypes.NETA21 || asset?.gatewayType === gatewayTypes.EGW02) && hasDataLoggerCommands),
            hidden: !((asset?.gatewayType === gatewayTypes.NETA21 || asset?.gatewayType === gatewayTypes.EGW02) && hasDataLoggerCommands)
        }
    ];
    const [headerMenus, setHeaderMenus] = useState([]);
    const [selectedHeaderMenu, setSelectedHeaderMenu] = useState();

    const selectedMenuItemId = useSelector(({ detailedInformation }) => detailedInformation.selectedMenuItem);

    useEffect(() => {
        setSelectedHeaderMenu(find(headerMenus, { id: selectedMenuItemId }));
    }, [selectedMenuItemId]);

    useEffect(() => {
        const menuItemsToRender = transform(allHeaderMenus, (acc, menu) => {
            if (!menu.availableCapabilityFn || menu.availableCapabilityFn(capability) && !menu.hidden) {
                acc.push({ ...menu });
            }
        }, []);
        setHeaderMenus(orderBy(menuItemsToRender, 'title'));
    }, [selectedAssetTypeId, capability, dataLoggerCommands, asset, selectedAssetType, hasInstantReportHidden, hasDataLoggerCommands]);

    return [headerMenus, selectedHeaderMenu];
};

export default useHeaderMenus;
